/**
 * Microphone capture profiles for the recording pipeline.
 *
 * The browser applies a DSP chain (echo cancellation, noise suppression, gain
 * control) to every `getUserMedia` stream, on top of whatever the OS and the
 * audio driver already do. That chain is tuned for one voice close to the mic:
 * it treats everything that is not the dominant near-field speaker as noise. In
 * a room with several people it therefore gates the far-field voices away, and
 * what reaches the diarizer is a single, flattened speaker — the machine-
 * dependent half of "geen verschillende sprekers" (#3285).
 *
 * Rather than guessing per device, the caller picks a profile:
 * - `close-talk` — one person at a headset/laptop mic (the historical default).
 * - `room` — several people in the room; the suppressors are switched off so
 *   quiet, far-away voices survive to the diarizer.
 * - `online` — a session held over a video call. The microphone is captured as
 *   for `close-talk`, and the call's own audio is captured beside it from a
 *   shared tab or screen (see `callAudioCapture`) and mixed in (#4638).
 *
 * The sample rate and channel count are fixed for every profile: the WAV header
 * written by `audioAssembler` and the streaming contract with the whisper
 * service (ADR-018) both assume 16 kHz mono PCM.
 */

/** Capture sample rate (Hz) assumed by the WAV header and the whisper service. */
export const CAPTURE_SAMPLE_RATE = 16000

/** Capture channel count assumed by the WAV header and the whisper service. */
export const CAPTURE_CHANNEL_COUNT = 1

/** Which DSP trade-off the capture should make. */
export type CaptureProfile = 'close-talk' | 'room' | 'online'

/** Profile used unless the behandelaar says the session is a multi-person room. */
export const DEFAULT_CAPTURE_PROFILE: CaptureProfile = 'close-talk'

/**
 * Build the `audio` constraints for a capture profile.
 *
 * `room` asks for automatic gain control explicitly: unlike the suppressors, AGC
 * *lifts* distant voices instead of gating them, which is what a far-field
 * microphone needs. Echo cancellation is dropped along with noise suppression
 * because it models a single near-end talker and ducks the others together with
 * the echo; a face-to-face session has no speaker playback for it to cancel.
 *
 * `close-talk` leaves AGC unconstrained so it keeps behaving exactly as the
 * pipeline always has — per-browser defaults, which differ.
 *
 * `online` shares `close-talk`'s constraints, and the echo cancellation in them
 * is load-bearing rather than a default: Chrome's canceller removes what *any*
 * Chrome tab plays, so a browser call over speakers does not reach the
 * microphone a second time. It cannot remove a desktop app's playback, which is
 * why the UI recommends a headset there (#4638).
 *
 * @param profile - Capture profile to build constraints for
 * @returns Constraints suitable for `getUserMedia({ audio })`
 */
export function buildAudioConstraints(profile: CaptureProfile): MediaTrackConstraints {
  const base: MediaTrackConstraints = {
    sampleRate: CAPTURE_SAMPLE_RATE,
    channelCount: CAPTURE_CHANNEL_COUNT,
  }

  if (profile === 'room') {
    return { ...base, echoCancellation: false, noiseSuppression: false, autoGainControl: true }
  }

  return { ...base, echoCancellation: true, noiseSuppression: true }
}
